import React, { useContext, useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { axiosPost } from "../../utils/AxiosHelper";
import toast from "react-hot-toast";
import { ESDataTable } from "../../controls/ESDataTable";
import {
  deleteUserDepartment,
  getAllUserDepartments,
} from "../../utils/ApiUrl";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import AddUserDepartment from "./AddUserDepartment";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  CardHeader,
  InputGroup,
  Button,
} from "react-bootstrap";
import Header from "../header/Header";
import SubHeader from "../subheader/Subheader";
import Footer from "../footer/Footer";
import "./UserDepartmentList.scss";
import searchico from "../../assets/images/search.svg";
import addico from "../../assets/images/add.svg";
import editico from "../../assets/images/edit.svg";
import trashico from "../../assets/images/trash.svg";
import crossico from "../../assets/images/close.svg";
import ExportExcel from "../Common/ExportExcel/ExportExcel";
import ESTooltip from "../../controls/ESTooltip";
import { FormProvider, useForm } from "react-hook-form";
import {
  Option,
  PageRecordLimit,
  fetchOrganization,
  formatDateCommon,
  getCachedData,
  queryClient,
  setPageRecordLimit,
} from "../../utils/common";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { checkIfSuperAdmin } from "../../utils/common";
import { UserContext } from "../../routes/routes";
import { ToastTypeCode, showToast, ToastPosition } from "../../utils/Constant";

interface UserDepartment {
  DepartmentId: number;
  DepartmentName: string;
  IsActive: number;
  CreateUserId: number;
  CreateDate: string;
  OrganizationId: number;
}

interface CustomTableColumn<T> extends TableColumn<T> {
  onClick?: () => void;
}

const UserDepartmentList: React.FC = () => {
  const [userDepartments, setUserDepartments] = useState<UserDepartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PageRecordLimit);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState<string>("CreateDate");
  const [sortDir, setSortDir] = useState<string>("DESC");
  const [searchText, setSearchText] = useState<string>("");
  const [showPopup, setShowPopup] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const methods = useForm();
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [organizationDropDownChange, setOrganizationDropDownChange] =
    useState(false);
  const [defaultOraganizationDropDown, setDefaultOraganizationDropDown] =
    useState<any>(null);
  const [isAddIconVisible, setIsAddIconVisible] = useState(true);

  const fetchUserDepartments = async (organizationidFromProps?: any) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await axiosPost(getAllUserDepartments, {
        SearchText: searchText,
        IsActive: true,
        CurrentPageNo: currentPage,
        PageSize: pageSize,
        SortDir: sortDir,
        SortBy: sortBy,
        OrganizationId: organizationidFromProps
          ? organizationidFromProps
          : !organizationDropDownChange && organizationId === 0
          ? -1
          : organizationId,
      });
      const data = response?.data?.ExtraData;
      setUserDepartments(data);
      setTotalRecords(data?.length > 0 ? data[0].TotalRecords : 0);
    } catch (error) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditId(id);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditId(null);
    setShowForm(true);
    setIsAddIconVisible(false);
  };

  const handleFormClose = () => {
    setShowForm(false);
    fetchUserDepartments();
    setIsAddIconVisible(true);
  };

  const handleDelete = async (id: number) => {
    if (id) {
      try {
        const response: any = await axiosPost(deleteUserDepartment, {
          DepartmentId: id,
        });
        if (response?.data?.Status === 200) {
          setUserDepartments((prevState) =>
            prevState.filter((item) => item.DepartmentId !== id)
          );
          queryClient.invalidateQueries("departments");
          showToast(
            "User department deleted successfully",
            ToastTypeCode?.SUCCESS
          );
        } else {
          showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
        }
        setShowPopup(false);
        fetchUserDepartments();
      } catch (error) {
        showToast("Error deleting user department", ToastTypeCode?.ERROR);
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    if (Object.keys(column)?.length === 0 && column.constructor === Object) {
      return;
    }
    setSortBy(column.sortField);
    setSortDir(sortDirection.toUpperCase());
  };

  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUserDepartments();
  };

  const handlePerRowsChange = async (newPerPage: any, page: any) => {
    setPageSize(newPerPage);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowPopup(true);
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);
      if (!isAdmin) {
        fetchUserDepartments(appContextdata?.Organization);
        setPageRecordLimit(pageSize);
      }
    }
  }, [appContextdata?.Role, currentPage, pageSize, sortBy, sortDir]);

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setDefaultOraganizationDropDown(null);
    setOrganizationId(selectedOption?.value ?? 0);
    setCurrentPage(1);
    setOrganizationDropDownChange(true);
  };

  useEffect(() => {
    if (organizationDropDownChange) {
      fetchUserDepartments();
      setPageRecordLimit(pageSize);
    }
  }, [organizationId, currentPage, pageSize, sortBy, sortDir]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData("organizations")) as Option[];
        setOrganizations(options);
        const defaultOption: any = options?.find(
          (option) => option.value === appContextdata?.Organization
        );
        handleOrganizationChange(defaultOption);
        setDefaultOraganizationDropDown(defaultOption);
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const columns: CustomTableColumn<UserDepartment>[] = [
    {
      name: "Actions",
      cell: (row) => (
        <>
          <ESTooltip text="Edit Department" placement="top">
            <button
              onClick={() => handleEdit(row.DepartmentId)}
              className="btnicon"
            >
              <img src={editico} alt="Edit" data-tour="edit-department" />
            </button>
          </ESTooltip>
          <ESTooltip text="Delete Department" placement="top">
            <button
              onClick={() => confirmDelete(row.DepartmentId)}
              className="btnicon"
            >
              <img src={trashico} alt="Delete" data-tour="delete-department" />
            </button>
          </ESTooltip>
        </>
      ),
      grow: 1,
      width: "80px",
    },
    {
      name: "Department Name",
      selector: (row) => row.DepartmentName,
      sortable: true,
      sortField: "DepartmentName",
      grow: 3,
    },
    {
      name: "Last Modified",
      selector: (row) => formatDateCommon(row.CreateDate),
      sortable: true,
      sortField: "CreateDate",
      grow: 1,
    },
  ];

  return (
    <div className="wrap-userdepartment">
      <Header />
      <SubHeader data={userDepartments} isAddIconVisible={isAddIconVisible} />
      <div className="userdepartment-blk">
        <Container fluid>
          <Card>
            <CardHeader>
              <Row>
                <Col lg={6}>
                  <h2 className="page-title">User Departments</h2>
                </Col>
                <Col lg={6}>
                  <div className="search-userdepartment">
                    {isSuperAdmin && (
                      <FormProvider {...methods}>
                        {organizations && organizations?.length >= 1 && (
                          <ESSearchDropdown
                            name="OrganizationId"
                            placeholder="Select Organization"
                            options={organizations}
                            onChange={handleOrganizationChange}
                            defaultValue={defaultOraganizationDropDown}
                          />
                        )}
                      </FormProvider>
                    )}

                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={searchText}
                        onChange={handleSearchTextChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search..."
                        className={"form-control search-bar"}
                      />
                      <InputGroup.Text onClick={handleSearch}>
                        <img
                          src={searchico}
                          alt="search"
                          data-tour="search-department"
                        />
                      </InputGroup.Text>
                    </InputGroup>
                    <div className="d-flex">
                      <ExportExcel
                        searchText={searchText}
                        model={userDepartments}
                        componentName={"UserDepartments"}
                        fileName={"UserDepartments"}
                      ></ExportExcel>
                      <ESTooltip
                        text={showForm ? "Close" : "Add Department"}
                        placement="top"
                      >
                        <Button
                          onClick={showForm ? handleFormClose : handleCreate}
                          className="primary-btn btn-add"
                        >
                          {showForm ? (
                            <img
                              src={crossico}
                              className="closeico"
                              alt="close"
                            />
                          ) : (
                            <img
                              src={addico}
                              alt="add"
                              data-tour="add-department"
                            />
                          )}
                        </Button>
                      </ESTooltip>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {showForm && (
                <AddUserDepartment
                  departmentId={editId}
                  onClose={handleFormClose}
                />
              )}
              {error && <p>{error}</p>}
              <div className="table-responsive">
                <ESDataTable
                  columns={columns}
                  data={userDepartments}
                  paginationTotalRows={totalRecords}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  onChangeRowsPerPage={handlePerRowsChange}
                />
              </div>
              {showPopup && (
                <ConfirmationPopup
                  show={showPopup}
                  message="Are you sure you want to delete this department?"
                  onConfirm={() => handleDelete(deleteId)}
                  onCancel={() => setShowPopup(false)}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default UserDepartmentList;
